import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, CheckCircle, Calendar } from 'lucide-react';

import API_URL from '../utils/api';

const Attendance = () => {
    const { canManage } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAttendance();
    }, []);

    const fetchAttendance = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/attendance`, { withCredentials: true });
            // Sort by date descending
            const sorted = data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setAttendance(sorted);
        } catch (error) {
            logger.error('Error fetching attendance:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const styles = {
            present: 'bg-green-100 text-green-800 border-green-200',
            absent: 'bg-red-100 text-red-800 border-red-200',
            late: 'bg-amber-100 text-amber-800 border-amber-200',
            not_checked_in: 'bg-slate-100 text-slate-800 border-slate-200'
        };
        return <Badge className={`rounded-full px-3 py-0.5 capitalize ${styles[status] || styles.present}`}>{status.replace('_', ' ')}</Badge>;
    };

    const presentCount = attendance.filter(a => a.status === 'present').length;
    const lateCount = attendance.filter(a => a.status === 'late').length;
    const avgHours = attendance.length > 0
        ? (attendance.reduce((sum, a) => sum + (a.hours_worked || 0), 0) / (attendance.filter(a => a.hours_worked).length || 1)).toFixed(1)
        : '0.0';
    const presentPct = attendance.length > 0 ? Math.round((presentCount / attendance.length) * 100) : 0;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading attendance records...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="attendance-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Attendance Records
                </h1>
                <p className="text-slate-500 mt-1">
                    {canManage ? 'View all employee attendance' : 'Your attendance history'}
                </p>
            </div>

            {/* Summary Cards - Premium gradient tints */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-[#2563eb] to-[#3b82f6]" />
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Total Days</p>
                                <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    {attendance.length}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">Tracked records</p>
                            </div>
                            <div className="p-2.5 bg-blue-100 rounded-lg">
                                <Calendar className="h-5 w-5 text-[#2563eb]" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Present Days</p>
                                <p className="text-2xl font-extrabold text-emerald-600 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    {presentCount}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">{presentPct}% rate</p>
                            </div>
                            <div className="p-2.5 bg-emerald-100 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-emerald-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-amber-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-amber-500 to-orange-500" />
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Late Arrivals</p>
                                <p className="text-2xl font-extrabold text-amber-600 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    {lateCount}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">After grace period</p>
                            </div>
                            <div className="p-2.5 bg-amber-100 rounded-lg">
                                <Clock className="h-5 w-5 text-amber-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-violet-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Avg Hours/Day</p>
                                <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    {avgHours}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">Hours worked</p>
                            </div>
                            <div className="p-2.5 bg-violet-100 rounded-lg">
                                <Clock className="h-5 w-5 text-violet-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Attendance Table */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardHeader className="border-b border-slate-200 bg-slate-50/50">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Clock className="h-5 w-5 text-[#2563eb]" />
                        Attendance History
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="attendance-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    {canManage && <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>}
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Check In</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Check Out</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Hours Worked</th>
                                    <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendance.map((record) => (
                                    <tr key={record._id || `${record.employee_id}-${record.date}`} className="border-b border-slate-200 hover:bg-slate-50">
                                        {canManage && <td className="p-4 font-medium text-slate-900">{record.employee_name}</td>}
                                        <td className="p-4 text-slate-700">{formatDate(record.date)}</td>
                                        <td className="p-4 text-slate-700">{formatTime(record.check_in)}</td>
                                        <td className="p-4 text-slate-700">{formatTime(record.check_out)}</td>
                                        <td className="p-4 text-slate-700">
                                            {record.hours_worked ? `${record.hours_worked} hrs` : '-'}
                                        </td>
                                        <td className="p-4 text-center">{getStatusBadge(record.status)}</td>
                                    </tr>
                                ))}
                                {attendance.length === 0 && (
                                    <tr>
                                        <td colSpan={canManage ? 6 : 5} className="p-8 text-center text-slate-500">
                                            No attendance records found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Attendance;
